import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './PrintBookingDetail.module.scss';
import Spinner from 'components/UI/V3/Spinner';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import { Order } from '@spike/order-model';
import { useMemo } from 'react';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
    groupBookingDetailToViewModel,
    PrintBookingDetailAppointmentViewModel,
    PrintBookingDetailViewModel
} from './utils/PrintBookingDetailViewModel';
import PrintPetInfo from './PrintPetInfo/PrintPetInfo';
import { formatPhoneNumber } from '@spike/phone-utils';
import { faPhoneFlip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PrintAppointmentGrooming from './PrintAppointmentGrooming/PrintAppointmentGrooming';
import PrintAppointmentDaycare from './PrintAppointmentDaycare/PrintAppointmentDaycare';
import PrintAppointmentBoarding from './PrintAppointmentBoarding/PrintAppointmentBoarding';
import PrintAppointmentTransportation from './PrintAppointmentTransportation/PrintAppointmentTransportation';
import PrintAddOnsDay from './PrintAddOnsDay/PrintAddOnsDay';
import { getVetThunk } from '@spike/vets-action';
import { RootState } from 'store';
import Vet from '@spike/vet-model';
import PrintContactInfo from './PrintContactInfo/PrintContactInfo';
import PrintPetVaccines from './PrintPetHealth/PrintPetVaccines';
import PrintPetMedicalConditions from './PrintPetHealth/PrintPetMedicalConditions';
import PrintPetBelongings from './PrintPetBelongings/PrintPetBelongings';
import { BookingBelonging, BookingBelongingsPage } from '@spike/belonging-model';
import { BelongingsStatus } from '@spike/belongings-action';

export interface PrintBookingDetailProps {
    order: Order;
    bookingId?: number;
    onClose: () => void;
    onAfterClose?: () => void;
}
function usePrint(isReadyToPrint: boolean, onClose: () => void, onAfterClose?: () => void) {
    useHideHubSpotChat(true);

    useEffect(() => {
        if (!isReadyToPrint) return;

        document.body.classList.add('printing');

        const timer = setTimeout(() => {
            window.print();
        }, 300);

        const handleAfterPrint = () => {
            document.body.classList.remove('printing');
            onClose();
            onAfterClose?.();
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
            document.body.classList.remove('printing');
        };
    }, [isReadyToPrint, onClose, onAfterClose]);

    // useHideHubSpotChat(false);
}

export const PrintBookingDetail: React.FC<PrintBookingDetailProps> = ({ order, bookingId, onClose, onAfterClose }) => {
    const [isReadyToPrint, setIsReadyToPrint] = useState(false);
    const [footerLoaded, setFooterLoaded] = useState(false);

    const timezone = useTimeZone();
    const marketplace = useMarketplace();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const vm = useMemo(() => {
        if (!order) return [];
        return groupBookingDetailToViewModel(order, timezone);
    }, [order, timezone]);

    const currentVet = useSelector<RootState, Vet | undefined>(state => state.vets.vet || state.vets.savedVet);

    const [vetsById, setVetsById] = useState<Record<number, Vet | undefined>>({});
    const [requestedVetIds, setRequestedVetIds] = useState<Record<number, true>>({});

    const vetIds = useMemo(() => {
        const ids = new Set<number>();
        vm.forEach(section => {
            const id = section.pet?.vetId;
            if (typeof id === 'number') ids.add(id);
        });
        return Array.from(ids);
    }, [vm]);

    useEffect(() => {
        if (!vetIds.length) return;

        vetIds.forEach(vetId => {
            const alreadyCached = !!vetsById[vetId];
            const alreadyRequested = !!requestedVetIds[vetId];
            if (alreadyCached || alreadyRequested) return;

            setRequestedVetIds(prev => ({ ...prev, [vetId]: true }));
            dispatch(getVetThunk(apiClientWrapper, vetId));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vetIds, vetsById, requestedVetIds]);

    useEffect(() => {
        if (!currentVet?.id) return;

        const id = currentVet.id;

        if (!requestedVetIds[id]) return;

        setVetsById(prev => (prev[id] ? prev : { ...prev, [id]: currentVet }));
    }, [currentVet, requestedVetIds]);

    const bookingBelongingsPage = useSelector<RootState, BookingBelongingsPage | undefined>(
        state => state.belongings.bookingBelongingsPage
    );
    const belongingsStatus = useSelector<RootState, BelongingsStatus>(state => state.belongings.status);
    const belongingsReady = belongingsStatus !== BelongingsStatus.BookingBelongingsFetching;

    const belongingsByPetId = useMemo(() => {
        const byPet = new Map<number, BookingBelonging>();
        (bookingBelongingsPage?.data ?? [])
            .filter(entry => entry.bookingId === bookingId)
            .forEach(entry => byPet.set(entry.petId, entry));
        return byPet;
    }, [bookingBelongingsPage, bookingId]);

    const needsVet = vetIds.length > 0;
    const vetReady = !needsVet || vetIds.every(id => !!vetsById[id]);

    useEffect(() => {
        if (footerLoaded && vetReady && belongingsReady) setIsReadyToPrint(true);
    }, [footerLoaded, vetReady, belongingsReady]);

    usePrint(isReadyToPrint, onClose, onAfterClose);

    const renderAppointmentByBusinessArea = (
        appt: PrintBookingDetailAppointmentViewModel,
        dateLabel: string,
        showDivider: boolean
    ) => {
        switch (appt.businessAreaId) {
            case 'grooming':
            case 'dental':
                return (
                    <PrintAppointmentGrooming
                        key={appt.id}
                        appointment={appt}
                        dateLabel={dateLabel}
                        showDivider={showDivider}
                    />
                );

            case 'daycare':
                return (
                    <PrintAppointmentDaycare
                        key={appt.id}
                        appointment={appt}
                        dateLabel={dateLabel}
                        showDivider={showDivider}
                    />
                );

            case 'boarding':
                return (
                    <PrintAppointmentBoarding
                        key={appt.id}
                        appointment={appt}
                        dateLabel={dateLabel}
                        showDivider={showDivider}
                    />
                );

            case 'transportation':
                return (
                    <>
                        <PrintAppointmentTransportation
                            key={appt.id}
                            appointment={appt}
                            dateLabel={dateLabel}
                            showDivider={showDivider}
                        />
                    </>
                );

            default:
                return (
                    <PrintAppointmentGrooming
                        key={appt.id}
                        appointment={appt}
                        dateLabel={dateLabel}
                        showDivider={showDivider}
                    />
                );
        }
    };

    const header = (
        <div className={styles.printHeader}>
            <span className={styles.customerName}>
                {order.client.firstName} {order.client.lastName}
            </span>
            <div className={styles.phoneContent}>
                <FontAwesomeIcon
                    size="sm"
                    icon={faPhoneFlip}
                    className={styles.phoneIcon}
                />
                <span className={styles.customerPhone}>
                    {formatPhoneNumber(order.client.phone, marketplace.basics.address.country!.id)}
                </span>
            </div>
        </div>
    );

    const content = (
        <div className={styles.printContent}>
            {vm.map((section: PrintBookingDetailViewModel) => {
                const vet = section.pet?.vetId ? vetsById[section.pet.vetId] : undefined;
                const petBelonging = section.pet?.id != null ? belongingsByPetId.get(section.pet.id) : undefined;

                return (
                    <div key={section.pet?.id ?? section.pet?.uuid}>
                        <PrintPetInfo pet={section.pet} />
                        <PrintContactInfo
                            client={order.client}
                            countryId={marketplace.basics.address.country!.id}
                            vet={vet}
                        />

                        {section.groups.map(group => {
                            const hasAddOns = group.addOns.length > 0;
                            const lastIndex = group.appointments.length - 1;

                            return (
                                <div key={group.dateKey}>
                                    {group.appointments.map((appt, idx) => {
                                        const isLast = idx === lastIndex;
                                        const showDivider = !(hasAddOns && isLast);
                                        return renderAppointmentByBusinessArea(appt, group.dateLabel, showDivider);
                                    })}

                                    {hasAddOns && <PrintAddOnsDay addOns={group.addOns} />}
                                </div>
                            );
                        })}
                        <PrintPetVaccines pet={section.pet} />
                        <PrintPetMedicalConditions pet={section.pet} />
                        <PrintPetBelongings bookingBelonging={petBelonging} />
                    </div>
                );
            })}
        </div>
    );

    const footer = (
        <div className={styles.printFooter}>
            <img
                src="/images/logo/poweredByCuddles.png"
                className={styles.footerLogo}
                onLoad={() => setFooterLoaded(true)}
                alt="Powered by Cuddles"
            />
        </div>
    );

    return ReactDOM.createPortal(
        <div className={styles.printOverlay}>
            {!isReadyToPrint ? (
                <div className={styles.spinnerOverlay}>
                    <Spinner />
                    {footer}
                </div>
            ) : (
                <>
                    {header}
                    {content}
                    {footer}
                </>
            )}
        </div>,
        document.body
    );
};

export default PrintBookingDetail;
